/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { AuthenticationResult } from "@azure/msal-browser";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import localStorage from "redux-persist/es/storage";

import AppProviders from "../../AppProviders";
import { useAcquireToken } from "../../features/azure-b2c/hooks/useAcquireToken";
import { useAcquireTokenOnFocus } from "../../features/azure-b2c/hooks/useAcquireTokenOnFocus";
import { useRefreshPermissionsOnTokenChange } from "../../features/azure-b2c/hooks/useRefreshPermissionsOnTokenChange";
import { instantiateCombinedClient } from "../../features/azure-b2c/NativeNavigationClient";
import AuthSlice, {
  getAccountDetails,
  getAccounts,
  getUserDetails,
  selectCurrentAccountNumber,
  selectCurrentFarmerAccount,
  selectIsCEUser,
  selectIsLoadingAuth,
} from "../../features/azure-b2c/storage";
import { getStores } from "../../features/stores/storage/stores.slice";
import apiClient from "../../services/api-client";
import { setFarmlandsProApiGetAccessToken } from "../../services/farmlandspro-api";
import { setMyFarmlandsApiGetAccessToken } from "../../services/myfarmlands-api";
import { useAppDispatch } from "../../store/rootStore";
import { error } from "../../util/log";
import { isPlatformMobile } from "../../util/platform";
import useMsalFunctions from "../../util/useMsalAuthFunctions";
import { useScrollToTop } from "../../util/useScrollToTop";
import { Loader } from "../Loader";

import AuthenticatedRoutes from "./AuthenticatedRoutes";
import NoAccessRoutes from "./NoAccessRoutes";
import UnauthenticatedRoutes from "./UnauthenticatedRoutes";

const AppRoutes = () => {
  const {
    instance,
    inProgress,
    accounts,
    resetMsalInstance,
    handleMsalLogout,
    handleCEMsalLogout,
    createTokenRequest,
    createCELinkTokenRequest,
  } = useMsalFunctions();

  const navigate = useNavigate();
  const navigationClient = useCallback(() => instantiateCombinedClient(navigate), [navigate]);
  const dispatch = useAppDispatch();
  const [initialized, setInitialized] = useState(false);
  const [tokenFetched, setTokenFetched] = useState(false);
  const farmerAccount = useSelector(selectCurrentFarmerAccount);
  const isLoadingAuth = useSelector(selectIsLoadingAuth);
  const isCEUser = useSelector(selectIsCEUser);
  const ref = useRef<HTMLElement>(null);
  useScrollToTop({ ref });

  const logout = isCEUser ? handleCEMsalLogout : handleMsalLogout;

  const acquiringTokenCallback = useAcquireToken({
    instance,
    inProgress,
    accounts,
    createTokenRequest,
    createCELinkTokenRequest,
    handleMsalLogout: logout,
    resetMsalInstance,
    onTokenAcquired: () => setTokenFetched(true),
  });

  // A tab left idle fires no request, so nothing else would renew its token before the permissions
  // cache goes stale. Focus is what drives the renewal there.
  useAcquireTokenOnFocus(acquiringTokenCallback);

  // A renewed token is invisible to React Query — the permissions query keys on the account, not
  // the token — so the cache has to be invalidated explicitly for the new token to be used.
  useRefreshPermissionsOnTokenChange();

  const isTokenFetchedAndInitialized = tokenFetched && initialized;

  useEffect(() => {
    (async () => {
      if (accounts.length === 0) {
        return;
      }
      try {
        const token = await acquiringTokenCallback();
        if (token) {
          handleToken(token);
        }
      } catch (err) {
        // Nothing observes an async rejection here, and without a token `initialized` stays
        // false, holding the app on "Loading your account" for good. Sign out instead so the
        // user lands on a page they can act on, matching the monitor_window_timeout branch.
        error("Error acquiring token on startup:", err);
        await logout();
      }
    })();
  }, [accounts]);

  const handleToken = useCallback(
    (result: AuthenticationResult) => {
      if (result.account) {
        const account = result.account;
        if (account.idToken && !localStorage.getItem("loginTime")) {
          localStorage.setItem("loginTime", Date.now().toString());
        }

        apiClient.setDefaultToken(result.idToken);
        dispatch(AuthSlice.actions.setAuthAccount(result.account));
        setInitialized(true);
        setTokenFetched(true);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    apiClient.setGetAccessToken(acquiringTokenCallback);
    const getAccessToken = async () => {
      const result = await acquiringTokenCallback();
      return result?.accessToken ?? "";
    };
    setMyFarmlandsApiGetAccessToken(getAccessToken);
    setFarmlandsProApiGetAccessToken(getAccessToken);
    instance.setNavigationClient(navigationClient());
  }, [acquiringTokenCallback, instance, navigationClient]);

  useEffect(() => {
    if (!isPlatformMobile()) {
      const handleRedirectResponse = async () => {
        const redirectResponse = await instance.handleRedirectPromise();
        if (redirectResponse !== null) {
          handleToken(redirectResponse);
        }
      };
      handleRedirectResponse();
    }
  }, [instance, dispatch]);

  useEffect(() => {
    if (
      // Accounts are msal accounts, not farmlands 'accounts'
      accounts.length > 0 &&
      isTokenFetchedAndInitialized
    ) {
      dispatch(getAccounts());
      dispatch(getUserDetails());
      dispatch(getStores());
    }
  }, [isTokenFetchedAndInitialized, accounts]);

  const selectedAccountNumber = useSelector(selectCurrentAccountNumber);
  useEffect(() => {
    if (
      // Accounts are msal accounts, not farmlands 'accounts'
      accounts.length > 0 &&
      isTokenFetchedAndInitialized &&
      selectedAccountNumber
    ) {
      dispatch(getAccountDetails());
    }
  }, [isTokenFetchedAndInitialized, accounts, selectedAccountNumber]);

  return (
    <>
      <AuthenticatedTemplate>
        <AppProviders
          ready={isTokenFetchedAndInitialized}
          fallback={<Loader title="Loading your account" />}
        >
          {isLoadingAuth ? (
            <Loader title="Loading your account" />
          ) : farmerAccount &&
            (farmerAccount.hasShareholderAccess || farmerAccount.hasTfoAccess) ? (
            <AuthenticatedRoutes />
          ) : (
            <NoAccessRoutes />
          )}
        </AppProviders>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <UnauthenticatedRoutes />
      </UnauthenticatedTemplate>
    </>
  );
};

export default AppRoutes;
